import {
  DynamicFormRequiredType,
  IconButton,
  OdinIconSize,
  type DialogRef,
  type OdinKey,
} from '@myosh/odin-components';
import clsx from 'clsx';
import React, { useRef } from 'react';
import { useTranslation } from 'react-i18next';
import type { ExtendedDynamicFormField } from '../../../@types/forms';
import { useAppDispatch, useAppSelector } from '../../../redux/hooks';
import { useFieldOptionsQuery } from '../../../redux/services/field';
import {
  deleteGroupOrField,
  selectFormId,
  selectIsFieldSelected,
  setSelectedField,
  type FormBuilderField,
} from '../../../redux/slices/form-builder-slice';
import ConfirmationDialog from '../../common/confirmation-dialog.component';
import { extendFieldProps, formField, useFieldDeleteCentered } from '../form-builder-utils';

export interface FormBuilderDynamicFieldProps {
  field: FormBuilderField;
  groupId?: OdinKey;
  className?: string;
}

function FormBuilderDynamicField({ field, groupId, className }: FormBuilderDynamicFieldProps) {
  const dispatch = useAppDispatch();
  const { t } = useTranslation();
  const formId = useAppSelector(selectFormId);
  const fieldId = field.id ?? field.uuid;

  const isFieldSelected = useAppSelector((state) => selectIsFieldSelected(state, fieldId));
  const deleteModalRef = useRef<DialogRef>(null);
  const fieldContainerRef = useRef<HTMLDivElement>(null);

  const centerDeleteButton = useFieldDeleteCentered(fieldContainerRef);

  const { data } = useFieldOptionsQuery(
    {
      formId: formId ?? 0,
      queryId: field.id ?? 0,
      limit: 100,
    },
    {
      skip: !formId || !field?.id || !['OPTIONGROUP', 'MULTISELECTCHECKBOX'].includes(field.fieldType),
    }
  );

  const fieldProps: Record<string, unknown> = {
    ...field,
    ...field.dynamicProperties,
    label: field.fieldCaption?.translations?.[0]?.value || '',
    name: String(field.id),
    data: data?.items,
    readOnly: false,
    required: field.required ? DynamicFormRequiredType.True : DynamicFormRequiredType.False,
    disabled: true,
    ...extendFieldProps(field as ExtendedDynamicFormField),
  };

  const onFieldClick = () => {
    if (groupId) {
      dispatch(setSelectedField({ ...field, groupId }));
    }
  };

  const onDeleteHandler = (event: React.MouseEvent<HTMLElement>) => {
    event.stopPropagation();
    event.preventDefault();

    if (fieldId) {
      deleteModalRef.current?.show();
    }
  };

  const handleConfirmDelete = () => {
    if (fieldId) {
      dispatch(deleteGroupOrField({ type: 'FIELD', id: fieldId }));
    }
  };

  const handleCloseModal = () => {
    deleteModalRef.current?.close();
  };

  const FIELD = formField[field.fieldType]?.Component;
  const fieldClasses = clsx(
    'flex items-center mb-4 p-1 relative cursor-pointer rounded outline-primary-2 outline-2 hover:outline hover:opacity-50 [&:has(.delete-button:hover)]:outline-none [&:has(.delete-button:hover)]:opacity-100 active:cursor-grabbing',
    {
      'opacity-50 outline': isFieldSelected,
    },
    className
  );

  return (
    <>
      {FIELD && (
        <div className={fieldClasses} onClick={onFieldClick}>
          <div className="flex-grow [&_*]:pointer-events-none" ref={fieldContainerRef}>
            <FIELD {...fieldProps} />
          </div>
          <div className="pointer-events-auto ml-2 flex h-full flex-col items-center gap-2">
            {String(field.help?.translations?.[0]?.value ?? '').length > 0 && (
              <div className="h-4 w-4 rounded-full bg-info text-center text-xs font-bold text-mono-1">?</div>
            )}

            <IconButton
              icon="Trash"
              iconSize={OdinIconSize.Medium}
              iconClassName="w-5 h-5 mx-2"
              classNames={clsx('delete-button mr-4 h-6 w-4', {
                'mt-6': centerDeleteButton, // only offset if label exists
              })}
              onClick={onDeleteHandler}
            />
          </div>
        </div>
      )}

      <ConfirmationDialog
        ref={deleteModalRef}
        header={t('this-action-cannot-be-undone')}
        onConfirm={handleConfirmDelete}
        onClose={handleCloseModal}
        confirmButtonText={t('yes')}
        cancelButtonText={t('no')}
        hasCancelButton
      >
        <p>{t('field-group-delete-message')}</p>
      </ConfirmationDialog>
    </>
  );
}

export default React.memo(FormBuilderDynamicField);
